import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../bizsuite-app';
import { useFormDirty } from '../hooks/useFormDirty';
import TransitMaster from './TransitMaster';

/* ── SVG Icons ── */
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
);
const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
);
const IconSave = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
);
const IconChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
);
const IconX = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);
const IconUpload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
);
const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
);
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
);
const IconCancel = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);

/* ── Multi-select dropdown component ── */
function MultiSelect({ options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleOpen() {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setFlipUp(spaceBelow < 220);
    }
    setOpen(!open);
  }

  function toggle(id) {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
  }

  const selectedNames = options.filter(o => selected.includes(o.id)).map(o => o.name);

  return (
    <div className="multi-select" ref={ref}>
      <div className="multi-select-trigger" onClick={handleOpen}>
        {selectedNames.length === 0 ? (
          <span className="multi-select-placeholder">{placeholder || 'Select...'}</span>
        ) : (
          <div className="multi-select-tags">
            {selectedNames.map((name, i) => (
              <span key={selected[i]} className="multi-select-tag">
                {name}
                <span className="multi-select-tag-x" onClick={e => { e.stopPropagation(); toggle(selected[i]); }}><IconX /></span>
              </span>
            ))}
          </div>
        )}
        <span className="multi-select-arrow"><IconChevronDown /></span>
      </div>
      {open && (
        <div className={`multi-select-dropdown${flipUp ? ' flip-up' : ''}`}>
          {options.length === 0 && <div className="multi-select-empty">No options</div>}
          {options.map(o => (
            <label key={o.id} className={`multi-select-option${selected.includes(o.id) ? ' checked' : ''}`}>
              <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
              {o.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Max Score options ── */
const MAX_SCORE_OPTIONS = ['L1', 'L2', 'L3', 'L4', 'L5', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const TABS = [
  { key: 'products', label: 'Products' },
  { key: 'modules', label: 'Skill Groups' },
  { key: 'skillsets', label: 'Skillsets' },
  { key: 'lookups', label: 'Lookups' },
];

/* Lookup sub-categories — hierarchy: Vertical → Industry → Sub-Vertical */
const LOOKUP_CATEGORIES = [
  { key: 'vertical', label: 'Vertical' },
  { key: 'industry', label: 'Industry', parentCategory: 'vertical' },
  { key: 'sub_vertical', label: 'Sub-Vertical', parentCategory: 'industry' },
  { key: 'legal_entity', label: 'Legal Entity' },
  { key: 'size', label: 'Size' },
  { key: 'management_type', label: 'Management Type' },
  { key: 'division', label: 'Division' },
  { key: 'distribution_channel', label: 'Distribution Channel' },
  { key: 'role', label: 'Role' },               // Cost Sheet role dropdown master
  { key: 'country', label: 'Country' },         // Parent of Region — drives the cascade
  { key: 'region',  label: 'Region', parentCategory: 'country' },
  // Payment terms — uses the extra `days` column so invoices can compute
  // due dates from this single master.
  { key: 'payment_terms', label: 'Payment Terms', hasDays: true },
  // Calendars — each row defines which weekdays are working. Used by the
  // reminder scheduler to prepone fires that fall on weekly-offs/holidays.
  { key: 'calendar', label: 'Calendars', hasWorkdays: true },
  { key: 'transit', label: 'Transit', special: true },
];

/**
 * Inline 7-day chip selector for the Calendars lookup category. Shown only
 * when the lookup row's category supports `hasWorkdays`. Renders Mon–Sun as
 * toggleable chips; clicking flips that weekday between working and off.
 */
function WorkdayChips({ value = [], editing, onChange }) {
  const DAYS = [
    { v: 1, l: 'Mon' }, { v: 2, l: 'Tue' }, { v: 3, l: 'Wed' }, { v: 4, l: 'Thu' },
    { v: 5, l: 'Fri' }, { v: 6, l: 'Sat' }, { v: 7, l: 'Sun' },
  ];
  const set = new Set((value || []).map(Number));
  if (!editing) {
    const labels = DAYS.filter((d) => set.has(d.v)).map((d) => d.l);
    return <span style={{ fontSize: 12, color: '#475569' }}>{labels.length ? labels.join(', ') : '—'}</span>;
  }
  function toggle(v) {
    const next = set.has(v) ? [...set].filter((x) => x !== v) : [...set, v];
    onChange(next.sort((a, b) => a - b));
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {DAYS.map((d) => {
        const on = set.has(d.v);
        return (
          <button key={d.v} type="button" onClick={() => toggle(d.v)}
            style={{
              padding: '3px 9px', fontSize: 11, fontWeight: 600,
              borderRadius: 999, cursor: 'pointer',
              background: on ? '#3B82F6' : 'rgba(0,0,0,0.04)',
              color: on ? '#fff' : '#475569',
              border: `1px solid ${on ? '#3B82F6' : 'rgba(0,0,0,0.10)'}`,
            }}
          >{d.l}</button>
        );
      })}
    </div>
  );
}

export default function Metadata() {
  const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [modules, setModules] = useState([]);
  const [skillsets, setSkillsets] = useState([]);
  const [lookups, setLookups] = useState([]);
  const [lookupCategory, setLookupCategory] = useState('industry');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filterProductId, setFilterProductId] = useState('');
  const [filterModuleId, setFilterModuleId] = useState('');
  const [filterVerticalId, setFilterVerticalId] = useState('');
  const [editingId, setEditingId] = useState(null); // row id currently being edited

  /* ── Load data ── */
  const loadProducts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/metadata/products');
      const data = await res.json();
      setProducts(data.rows || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadModules = useCallback(async () => {
    try {
      const res = await apiFetch('/api/metadata/modules');
      const data = await res.json();
      setModules(data.rows || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadSkillsets = useCallback(async () => {
    try {
      const res = await apiFetch('/api/metadata/skillsets');
      const data = await res.json();
      setSkillsets(data.rows || []);
    } catch (e) { console.error(e); }
  }, []);

  const loadLookups = useCallback(async () => {
    try {
      const res = await apiFetch('/api/metadata/lookups');
      const data = await res.json();
      setLookups(data.rows || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadProducts(), loadModules(), loadSkillsets(), loadLookups()]).finally(() => setLoading(false));
  }, [loadProducts, loadModules, loadSkillsets, loadLookups]);

  /* ── Flash message ── */
  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  }

  /* ── Generic inline table helpers ── */
  function handleChange(rows, setRows, idx, key, value) {
    const updated = [...rows];
    updated[idx] = { ...updated[idx], [key]: value, _dirty: true };
    setRows(updated);
    markDirty();
  }

  function addRow(rows, setRows, defaults = {}) {
    const newId = '_new_' + Date.now();
    setRows([{ id: newId, _dirty: true, status: 'Active', ...defaults }, ...rows]);
    setEditingId(newId);
    markDirty();
  }

  async function deleteRow(endpoint, id, rows, setRows) {
    if (!id || String(id).startsWith('_new')) {
      setRows(rows.filter(r => r.id !== id));
      return;
    }
    try {
      await apiFetch(`/api/metadata/${endpoint}/${id}`, { method: 'DELETE' });
      setRows(rows.filter(r => r.id !== id));
      flash('Deleted');
      markDirty();
    } catch (e) {
      flash('Delete failed: ' + e.message, 'error');
    }
  }

  async function saveRows(endpoint, rows, setRows, reloadFn) {
    const dirtyRows = rows.filter(r => r._dirty);
    if (dirtyRows.length === 0) { flash('No changes to save'); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/metadata/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(dirtyRows),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash(`Saved ${data.saved?.length || 0} record(s)`);
      await reloadFn();
      setEditingId(null);
      resetDirty();
    } catch (e) {
      flash('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  /* Save a single row by id */
  async function saveOneRow(endpoint, rowId, rows, setRows, reloadFn) {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/metadata/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify([row]),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      flash('Saved');
      await reloadFn();
      setEditingId(null);
      resetDirty();
    } catch (e) {
      flash('Save failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit(rowId, rows, setRows) {
    if (String(rowId).startsWith('_new')) {
      setRows(rows.filter(r => r.id !== rowId));
    }
    setEditingId(null);
    resetDirty();
  }

  const isEditing = (id) => editingId === id;
  const isNewRow = (id) => String(id).startsWith('_new');

  /* Common cell styles */
  const cellText = { color: '#E2E8F0', fontSize: 13 };
  const cellMuted = { color: '#94A3B8', fontSize: 12.5, fontStyle: 'italic' };
  const statusBadge = (s) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11.5, fontWeight: 600,
    background: s === 'Active' ? 'rgba(52,211,153,0.12)' : 'rgba(148,163,184,0.12)',
    color: s === 'Active' ? '#34D399' : '#94A3B8',
  });

  /* ── Import Skillsets from Excel ── */
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  async function handleSkillsetImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      // Map columns flexibly — support multiple naming conventions
      const payload = rows.map(r => {
        const module = r['Module ID'] || r.Module || r.module || '';
        const skill = r['Skill Set Name'] || r['Skillset Name'] || r.Skill || r.skill || r.name || '';

        // Parse product names from "(Prod1) (Prod2) (Prod3)" format or comma-separated
        const rawProducts = r['Product ID'] || r.Product || r.Products || '';
        let productNames = [];
        if (typeof rawProducts === 'string' && rawProducts.includes('(')) {
          // Extract names from parentheses: "(SAP ECC) (SAP S/4HANA)" → ["SAP ECC", "SAP S/4HANA"]
          const matches = rawProducts.match(/\(([^)]+)\)/g);
          if (matches) productNames = matches.map(m => m.replace(/^\(|\)$/g, '').trim());
        } else if (typeof rawProducts === 'string' && rawProducts.trim()) {
          productNames = rawProducts.split(',').map(s => s.trim()).filter(Boolean);
        }

        // Keep the actual max score value (e.g. L5, 10)
        const maxScore = String(r['Max Score'] || r.max_score_scale || 'L5').trim();

        return { module, skill, product_names: productNames, max_score_scale: maxScore, status: r.Status || 'Active' };
      }).filter(r => r.module && r.skill);

      if (!payload.length) { flash('No valid rows found in file', 'error'); return; }

      const res = await apiFetch('/api/metadata/skillsets/seed', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (result.error) throw new Error(result.error);
      flash(`Imported ${result.created} skillset(s)${result.modulesCreated ? `, created ${result.modulesCreated} skill group(s)` : ''}${result.skipped ? `, ${result.skipped} skipped` : ''}`);
      await Promise.all([loadModules(), loadSkillsets()]);
    } catch (err) {
      flash('Import failed: ' + err.message, 'error');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  /* ════════════════════════════════════════════
     PRODUCTS TAB
     ════════════════════════════════════════════ */
  function renderProducts() {
    return (
      <div className="metadata-section">
        <div className="metadata-toolbar">
          <h3>Product Master</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="meta-btn meta-btn-outline" onClick={() => addRow(products, setProducts, { max_score: 'L3' })}>
              <IconPlus /> Add Product
            </button>
          </div>
        </div>
        <div className="data-table-scroll">
          <table className="data-table metadata-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 250 }}>Product Name</th>
                <th>Description</th>
                <th style={{ width: 120 }}>Max Score</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>No products. Click "Add Product" to start.</td></tr>
              )}
              {products.map((row, idx) => {
                const editing = isEditing(row.id);
                return (
                  <tr key={row.id} className={row._dirty ? 'row-dirty' : ''}>
                    <td style={{ color: '#64748B' }}>{idx + 1}</td>
                    <td>{editing
                      ? <input className="inline-input" value={row.name || ''} placeholder="Product name" onChange={e => handleChange(products, setProducts, idx, 'name', e.target.value)} />
                      : <span style={cellText}>{row.name || '—'}</span>}</td>
                    <td>{editing
                      ? <input className="inline-input" value={row.description || ''} placeholder="Description" onChange={e => handleChange(products, setProducts, idx, 'description', e.target.value)} />
                      : <span style={cellMuted}>{row.description || '—'}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.max_score || 'L3'} onChange={e => handleChange(products, setProducts, idx, 'max_score', e.target.value)}>{MAX_SCORE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select>
                      : <span style={cellText}>{row.max_score || 'L3'}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.status || 'Active'} onChange={e => handleChange(products, setProducts, idx, 'status', e.target.value)}><option>Active</option><option>Inactive</option></select>
                      : <span style={statusBadge(row.status || 'Active')}>{row.status || 'Active'}</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {editing ? (<>
                          <button className="btn-icon-sm success" title="Save" disabled={saving} onClick={() => saveOneRow('products', row.id, products, setProducts, loadProducts)}><IconCheck /></button>
                          <button className="btn-icon-sm" title="Cancel" onClick={() => cancelEdit(row.id, products, setProducts)}><IconCancel /></button>
                        </>) : (<>
                          <button className="btn-icon-sm" title="Edit" onClick={() => setEditingId(row.id)}><IconEdit /></button>
                          <button className="btn-icon-sm danger" title="Delete" onClick={() => deleteRow('products', row.id, products, setProducts)}><IconTrash /></button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     MODULES TAB  (multi-product selection)
     ════════════════════════════════════════════ */
  function renderModules() {
    const activeProducts = products.filter(p => p.status === 'Active' || !p.status);

    function handleProductsChange(idx, selectedIds) {
      const updated = [...modules];
      updated[idx] = { ...updated[idx], product_ids: selectedIds, _dirty: true };
      setModules(updated);
    }

    return (
      <div className="metadata-section">
        <div className="metadata-toolbar">
          <h3>Skill Group Master</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="meta-btn meta-btn-outline" onClick={() => addRow(modules, setModules, { product_ids: [] })}>
              <IconPlus /> Add Skill Group
            </button>
          </div>
        </div>
        <div className="data-table-scroll">
          <table className="data-table metadata-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 280 }}>Products</th>
                <th style={{ width: 140 }}>Type</th>
                <th style={{ width: 200 }}>Skill Group Name</th>
                <th>Description</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {modules.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>No skill groups. Add products first, then add skill groups.</td></tr>
              )}
              {modules.map((row, idx) => {
                const editing = isEditing(row.id);
                const prodNames = (row.product_ids || []).map(pid => {
                  const p = products.find(x => x.id === pid);
                  return p ? p.name : '';
                }).filter(Boolean).join(', ');
                return (
                  <tr key={row.id} className={row._dirty ? 'row-dirty' : ''}>
                    <td style={{ color: '#64748B' }}>{idx + 1}</td>
                    <td>{editing
                      ? <MultiSelect options={activeProducts} selected={row.product_ids || []} onChange={(ids) => handleProductsChange(idx, ids)} placeholder="Select Products..." />
                      : <span style={cellText}>{prodNames || '—'}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.module_type || 'Functional'} onChange={e => handleChange(modules, setModules, idx, 'module_type', e.target.value)}><option>Functional</option><option>Technical</option><option>Administration</option><option>Others</option></select>
                      : <span style={cellText}>{row.module_type || 'Functional'}</span>}</td>
                    <td>{editing
                      ? <input className="inline-input" value={row.name || ''} placeholder="Skill group name" onChange={e => handleChange(modules, setModules, idx, 'name', e.target.value)} />
                      : <span style={cellText}>{row.name || '—'}</span>}</td>
                    <td>{editing
                      ? <input className="inline-input" value={row.description || ''} placeholder="Description" onChange={e => handleChange(modules, setModules, idx, 'description', e.target.value)} />
                      : <span style={cellMuted}>{row.description || '—'}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.status || 'Active'} onChange={e => handleChange(modules, setModules, idx, 'status', e.target.value)}><option>Active</option><option>Inactive</option></select>
                      : <span style={statusBadge(row.status || 'Active')}>{row.status || 'Active'}</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {editing ? (<>
                          <button className="btn-icon-sm success" title="Save" disabled={saving} onClick={() => saveOneRow('modules', row.id, modules, setModules, loadModules)}><IconCheck /></button>
                          <button className="btn-icon-sm" title="Cancel" onClick={() => cancelEdit(row.id, modules, setModules)}><IconCancel /></button>
                        </>) : (<>
                          <button className="btn-icon-sm" title="Edit" onClick={() => setEditingId(row.id)}><IconEdit /></button>
                          <button className="btn-icon-sm danger" title="Delete" onClick={() => deleteRow('modules', row.id, modules, setModules)}><IconTrash /></button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     SKILLSETS TAB
     ════════════════════════════════════════════ */
  function renderSkillsets() {
    const activeProducts = products.filter(p => p.status === 'Active' || !p.status);
    const activeModules = modules.filter(m => m.status === 'Active' || !m.status);

    // Filter modules by selected product (using multi-product product_ids array)
    const filteredModules = filterProductId
      ? activeModules.filter(m => (m.product_ids || []).includes(filterProductId))
      : activeModules;

    let displaySkillsets = skillsets;
    if (filterProductId) {
      displaySkillsets = displaySkillsets.filter(s => {
        const mod = modules.find(m => m.id === s.module_id);
        return mod && (mod.product_ids || []).includes(filterProductId);
      });
    }
    if (filterModuleId) {
      displaySkillsets = displaySkillsets.filter(s => s.module_id === filterModuleId);
    }

    /* Helper: get valid products for a given module (from Module Master combos) */
    const getProductsForModule = (moduleId) => {
      if (!moduleId) return [];
      const mod = modules.find(m => m.id === moduleId);
      if (!mod) return [];
      const modProdIds = mod.product_ids || [];
      return activeProducts.filter(p => modProdIds.includes(p.id));
    };

    /* Helper: toggle a product id in the skillset's product_ids array */
    const toggleProduct = (rowIdx, productId) => {
      const realIdx = skillsets.indexOf(displaySkillsets[rowIdx]);
      const current = Array.isArray(skillsets[realIdx].product_ids) ? [...skillsets[realIdx].product_ids] : [];
      const idx = current.indexOf(productId);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(productId);
      handleChange(skillsets, setSkillsets, realIdx, 'product_ids', current);
    };

    return (
      <div className="metadata-section">
        <div className="metadata-toolbar">
          <h3>Skillset Master</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="meta-filter-select" value={filterProductId}
              onChange={e => { setFilterProductId(e.target.value); setFilterModuleId(''); }}>
              <option value="">All Products</option>
              {activeProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select className="meta-filter-select" value={filterModuleId}
              onChange={e => setFilterModuleId(e.target.value)}>
              <option value="">All Skill Groups</option>
              {filteredModules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button className="meta-btn meta-btn-outline" onClick={() => addRow(skillsets, setSkillsets, {
              module_id: filteredModules[0]?.id || '', product_ids: [], max_score_scale: 'L5'
            })}>
              <IconPlus /> Add Skillset
            </button>
            <input type="file" ref={fileInputRef} accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
              onChange={handleSkillsetImport} />
            <button className="meta-btn meta-btn-outline" disabled={importing}
              onClick={() => fileInputRef.current?.click()}>
              <IconUpload /> {importing ? 'Importing...' : 'Import Excel'}
            </button>
          </div>
        </div>
        <div className="data-table-scroll">
          <table className="data-table metadata-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 200 }}>Skill Group</th>
                <th style={{ width: 220 }}>Products</th>
                <th style={{ width: 200 }}>Skillset Name</th>
                <th style={{ width: 130 }}>Max Score</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {displaySkillsets.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>No skillsets. Add skill groups first, then add skillsets.</td></tr>
              )}
              {displaySkillsets.map((row, idx) => {
                const editing = isEditing(row.id);
                const availableModules = filterProductId ? filteredModules : activeModules;
                const moduleProducts = getProductsForModule(row.module_id);
                const rowProductIds = Array.isArray(row.product_ids) ? row.product_ids : [];
                const moduleName = modules.find(m => m.id === row.module_id)?.name || '—';
                const prodNames = rowProductIds.map(pid => { const p = products.find(x => x.id === pid); return p ? p.name : ''; }).filter(Boolean).join(', ');

                return (
                  <tr key={row.id} className={row._dirty ? 'row-dirty' : ''}>
                    <td style={{ color: '#64748B' }}>{idx + 1}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.module_id || ''} onChange={e => { const realIdx = skillsets.indexOf(row); handleChange(skillsets, setSkillsets, realIdx, 'module_id', e.target.value); handleChange(skillsets, setSkillsets, realIdx, 'product_ids', []); }}><option value="">-- Select Skill Group --</option>{availableModules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
                      : <span style={cellText}>{moduleName}</span>}</td>
                    <td>{editing ? (
                        moduleProducts.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {moduleProducts.map(p => {
                              const checked = rowProductIds.includes(p.id);
                              return (
                                <label key={p.id} style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5,
                                  padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                                  background: checked ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)',
                                  border: `1px solid ${checked ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.08)'}`,
                                  color: checked ? '#60A5FA' : '#94A3B8',
                                  transition: 'all 0.15s',
                                }}>
                                  <input type="checkbox" checked={checked}
                                    onChange={() => toggleProduct(idx, p.id)}
                                    style={{ width: 12, height: 12, accentColor: '#60A5FA' }} />
                                  {p.name}
                                </label>
                              );
                            })}
                          </div>
                        ) : <span style={{ color: '#475569', fontSize: 12, fontStyle: 'italic' }}>Select skill group first</span>
                      ) : <span style={cellText}>{prodNames || '—'}</span>}</td>
                    <td>{editing
                      ? <input className="inline-input" value={row.name || ''} placeholder="Skillset name" onChange={e => handleChange(skillsets, setSkillsets, skillsets.indexOf(row), 'name', e.target.value)} />
                      : <span style={cellText}>{row.name || '—'}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.max_score_scale || 'L5'} onChange={e => handleChange(skillsets, setSkillsets, skillsets.indexOf(row), 'max_score_scale', e.target.value)}><optgroup label="Level Scale"><option value="L1">L1</option><option value="L2">L2</option><option value="L3">L3</option><option value="L4">L4</option><option value="L5">L5</option></optgroup><optgroup label="Numeric Scale"><option value="5">5</option><option value="10">10</option></optgroup></select>
                      : <span style={cellText}>{row.max_score_scale || 'L5'}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.status || 'Active'} onChange={e => handleChange(skillsets, setSkillsets, skillsets.indexOf(row), 'status', e.target.value)}><option>Active</option><option>Inactive</option></select>
                      : <span style={statusBadge(row.status || 'Active')}>{row.status || 'Active'}</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {editing ? (<>
                          <button className="btn-icon-sm success" title="Save" disabled={saving} onClick={() => saveOneRow('skillsets', row.id, skillsets, setSkillsets, loadSkillsets)}><IconCheck /></button>
                          <button className="btn-icon-sm" title="Cancel" onClick={() => cancelEdit(row.id, skillsets, setSkillsets)}><IconCancel /></button>
                        </>) : (<>
                          <button className="btn-icon-sm" title="Edit" onClick={() => setEditingId(row.id)}><IconEdit /></button>
                          <button className="btn-icon-sm danger" title="Delete" onClick={() => deleteRow('skillsets', row.id, skillsets, setSkillsets)}><IconTrash /></button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     LOOKUPS TAB
     ════════════════════════════════════════════ */
  function renderLookups() {
    const catConfig = LOOKUP_CATEGORIES.find(c => c.key === lookupCategory);
    const catLabel = catConfig?.label || lookupCategory;
    const parentCategory = catConfig?.parentCategory;

    // Special case: Transit has its own dedicated table & UI
    if (lookupCategory === 'transit') {
      return (
        <div className="metadata-section">
          <div className="metadata-toolbar">
            <h3>Transit Master</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="meta-filter-select" value={lookupCategory}
                onChange={e => { if (confirmIfDirty()) { setLookupCategory(e.target.value); setFilterVerticalId(''); resetDirty(); } }}>
                {LOOKUP_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <TransitMaster embedded />
        </div>
      );
    }

    // Filter lookups for current category, optionally by parent
    let categoryRows = lookups.filter(l => l.category === lookupCategory);
    if (parentCategory && filterVerticalId) {
      categoryRows = categoryRows.filter(l => l.parent_id === filterVerticalId);
    }

    // If this category has a parent (e.g. sub_vertical → vertical), get parent options
    const parentOptions = parentCategory
      ? lookups.filter(l => l.category === parentCategory && (l.status === 'Active' || !l.status))
      : [];

    function handleLookupChange(idx, key, value) {
      const globalIdx = lookups.indexOf(categoryRows[idx]);
      if (globalIdx === -1) {
        // New row not yet in global — update by matching id
        const updated = lookups.map(l => l.id === categoryRows[idx].id ? { ...l, [key]: value, _dirty: true } : l);
        setLookups(updated);
      } else {
        const updated = [...lookups];
        updated[globalIdx] = { ...updated[globalIdx], [key]: value, _dirty: true };
        setLookups(updated);
      }
      markDirty();
    }

    function addLookupRow() {
      const newId = '_new_' + Date.now();
      const newRow = {
        id: newId,
        category: lookupCategory,
        parent_id: null,
        name: '',
        description: '',
        sort_order: categoryRows.length,
        status: 'Active',
        _dirty: true,
      };
      setLookups([newRow, ...lookups]);
      setEditingId(newId);
      markDirty();
    }

    function deleteLookupRow(row) {
      if (!row.id || String(row.id).startsWith('_new')) {
        setLookups(lookups.filter(l => l.id !== row.id));
        return;
      }
      deleteRow('lookups', row.id, lookups, setLookups);
    }

    async function saveLookupRows() {
      const dirtyRows = lookups.filter(r => r._dirty);
      if (dirtyRows.length === 0) { flash('No changes to save'); return; }
      await saveRows('lookups', dirtyRows, setLookups, loadLookups);
      resetDirty();
    }

    return (
      <div className="metadata-section">
        <div className="metadata-toolbar">
          <h3>{catLabel} Master</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="meta-filter-select" value={lookupCategory}
              onChange={e => { if (confirmIfDirty()) { setLookupCategory(e.target.value); setFilterVerticalId(''); resetDirty(); } }}>
              {LOOKUP_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {parentCategory && parentOptions.length > 0 && (
              <select className="meta-filter-select" value={filterVerticalId}
                onChange={e => setFilterVerticalId(e.target.value)}>
                <option value="">All {LOOKUP_CATEGORIES.find(c => c.key === parentCategory)?.label || 'Parent'}s</option>
                {parentOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <button className="meta-btn meta-btn-outline" onClick={addLookupRow}>
              <IconPlus /> Add {catLabel}
            </button>
          </div>
        </div>
        <div className="data-table-scroll">
          <table className="data-table metadata-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                {parentCategory && <th style={{ width: 200 }}>{LOOKUP_CATEGORIES.find(c => c.key === parentCategory)?.label || 'Parent'}</th>}
                <th style={{ width: 250 }}>Name</th>
                <th>Description</th>
                {catConfig?.hasDays && <th style={{ width: 90 }}>Days</th>}
                {catConfig?.hasWorkdays && <th style={{ width: 280 }}>Workdays</th>}
                <th style={{ width: 80 }}>Order</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {categoryRows.length === 0 && (
                <tr><td colSpan={(parentCategory ? 7 : 6) + (catConfig?.hasDays ? 1 : 0) + (catConfig?.hasWorkdays ? 1 : 0)} style={{ textAlign: 'center', padding: 24, color: '#64748B' }}>
                  No {catLabel.toLowerCase()} entries. Click "Add {catLabel}" to start.
                </td></tr>
              )}
              {categoryRows.map((row, idx) => {
                const editing = isEditing(row.id);
                const parentName = parentCategory && row.parent_id ? (parentOptions.find(p => p.id === row.parent_id)?.name || '—') : '—';
                return (
                  <tr key={row.id} className={row._dirty ? 'row-dirty' : ''}>
                    <td style={{ color: '#64748B' }}>{idx + 1}</td>
                    {parentCategory && (
                      <td>{editing
                        ? <select className="inline-input" value={row.parent_id || ''} onChange={e => handleLookupChange(idx, 'parent_id', e.target.value || null)}><option value="">-- Select --</option>{parentOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                        : <span style={cellText}>{parentName}</span>}</td>
                    )}
                    <td>{editing
                      ? <input className="inline-input" value={row.name || ''} placeholder={`${catLabel} name`} onChange={e => handleLookupChange(idx, 'name', e.target.value)} />
                      : <span style={cellText}>{row.name || '—'}</span>}</td>
                    <td>{editing
                      ? <input className="inline-input" value={row.description || ''} placeholder="Description" onChange={e => handleLookupChange(idx, 'description', e.target.value)} />
                      : <span style={cellMuted}>{row.description || '—'}</span>}</td>
                    {catConfig?.hasDays && (
                      <td>{editing
                        ? <input className="inline-input" type="number" min="0" value={row.days ?? 0} style={{ width: 70 }}
                            onChange={e => handleLookupChange(idx, 'days', e.target.value === '' ? null : parseInt(e.target.value, 10) || 0)} />
                        : <span style={cellText}>{row.days != null ? row.days : '—'}</span>}</td>
                    )}
                    {catConfig?.hasWorkdays && (
                      <td>
                        <WorkdayChips
                          value={Array.isArray(row.workdays) ? row.workdays : (typeof row.workdays === 'string' ? (() => { try { return JSON.parse(row.workdays); } catch (_) { return []; } })() : [])}
                          editing={editing}
                          onChange={(arr) => handleLookupChange(idx, 'workdays', arr)}
                        />
                      </td>
                    )}
                    <td>{editing
                      ? <input className="inline-input" type="number" value={row.sort_order || 0} style={{ width: 60 }} onChange={e => handleLookupChange(idx, 'sort_order', parseInt(e.target.value, 10) || 0)} />
                      : <span style={cellText}>{row.sort_order || 0}</span>}</td>
                    <td>{editing
                      ? <select className="inline-input" value={row.status || 'Active'} onChange={e => handleLookupChange(idx, 'status', e.target.value)}><option>Active</option><option>Inactive</option></select>
                      : <span style={statusBadge(row.status || 'Active')}>{row.status || 'Active'}</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {editing ? (<>
                          <button className="btn-icon-sm success" title="Save" disabled={saving} onClick={() => { saveLookupRows(); }}><IconCheck /></button>
                          <button className="btn-icon-sm" title="Cancel" onClick={() => { if (String(row.id).startsWith('_new')) { setLookups(lookups.filter(l => l.id !== row.id)); } setEditingId(null); resetDirty(); }}><IconCancel /></button>
                        </>) : (<>
                          <button className="btn-icon-sm" title="Edit" onClick={() => setEditingId(row.id)}><IconEdit /></button>
                          <button className="btn-icon-sm danger" title="Delete" onClick={() => deleteLookupRow(row)}><IconTrash /></button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════ */
  return (
    <div className="page-container">

      {msg && (
        <div className={`flash-msg ${msg.type}`}>{msg.text}</div>
      )}

      {/* Modern Tab bar */}
      <div className="meta-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`meta-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => { if (confirmIfDirty()) { setActiveTab(tab.key); resetDirty(); } }}
          >
            {tab.label}
            {tab.key === 'products' && products.length > 0 && <span className="meta-tab-badge">{products.length}</span>}
            {tab.key === 'modules' && modules.length > 0 && <span className="meta-tab-badge">{modules.length}</span>}
            {tab.key === 'skillsets' && skillsets.length > 0 && <span className="meta-tab-badge">{skillsets.length}</span>}
            {tab.key === 'lookups' && lookups.length > 0 && <span className="meta-tab-badge">{lookups.length}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
          Loading metadata...
        </div>
      ) : (
        <div className="card" style={{ marginTop: 0, borderTopLeftRadius: 0 }}>
          {activeTab === 'products' && renderProducts()}
          {activeTab === 'modules' && renderModules()}
          {activeTab === 'skillsets' && renderSkillsets()}
          {activeTab === 'lookups' && renderLookups()}
        </div>
      )}
    </div>
  );
}
